<template>
  <div>
    <v-card>
      <v-card-title>
        Layanan
        <v-spacer></v-spacer>
        <v-btn color="purple" dark class="mb-2" @click="doAdd"> Tambah </v-btn>
      </v-card-title>

      <v-dialog v-model="dialog" persistent max-width="600px">
        <v-card>
          <v-card-title>
            <span class="text-h5">Layanan</span>
          </v-card-title>
          <v-card-text>
            <v-form
              ref="form"
              v-model="isValid"
              class="pl-4 pr-4"
              @submit.prevent="doSave"
            >
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="currentData.nama"
                      label="Nama"
                      required
                      :rules="currentData.namaRules"
                    ></v-text-field>
                    <v-textarea
                      v-model="currentData.deskripsi"
                      label="Deskripsi"
                      clearable
                      dense
                      outlined
                      required
                      :rules="currentData.deskripsiRules"
                    ></v-textarea>
                    <v-text-field
                      v-model="currentData.nama_en"
                      label="Nama Inggris"
                      required
                      :rules="currentData.namaRules"
                    ></v-text-field>
                    <v-textarea
                      v-model="currentData.deskripsi_en"
                      label="Deskripsi Inggris"
                      clearable
                      dense
                      outlined
                      required
                      :rules="currentData.deskripsiRules"
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-container>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="dialog = false">
                  Close
                </v-btn>
                <v-btn color="blue darken-1" text type="submit"> Save </v-btn>
              </v-card-actions>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogDelete" max-width="500px">
        <v-card>
          <v-card-title class="text-h5"
            >Apa anda akan menghapus data ini?</v-card-title
          >
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="closeDelete">Tidak</v-btn>
            <v-btn color="blue darken-1" text @click="deleteItemConfirm"
              >Ya</v-btn
            >
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-row class="mx-4 my-4">
        <v-col cols="12" sm="4" md="4">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Cari Nama"
            single-line
            outlined
            dense
            hide-details
            @keyup.enter="getDataFromApi"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="list_data"
        :options.sync="options"
        :server-items-length="totalData"
        :loading="loading"
        class="elevation-1"
      >
        <template #[`item.actions`]="{ item }">
          <v-icon medium class="mr-3" @click="editItem(item)">
            mdi-pencil
          </v-icon>
          <v-icon medium @click="deleteItem(item)"> mdi-delete </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted, nextTick, defineProps } from "vue";
import LayananSuperiorLayananService from "../../services/master/LayananSuperiorLayananService";
import CommonService from "../../services/CommonService";

// --- Props ---
const props = defineProps({
  id: Number,
});

// --- Template Refs ---
const form = ref(null); // For v-form ref

// --- State ---
const isValid = ref(true);
const search = ref("");
const list_data = ref([]);
const totalData = ref(0);
const loading = ref(true);
const options = ref({ itemsPerPage: 10 });
const dialog = ref(false);
const dialogDelete = ref(false);
const editedIndex = ref(-1);
const editedItem = ref({});
const isFirstLoad = ref(false);

const headers = ref([
  { text: "No", align: "start", sortable: false, value: "no" },
  { text: "Nama", value: "nama", sortable: false },
  { text: "Actions", value: "actions", sortable: false },
]);

const defaultData = {
  id: null,
  nama: "",
  deskripsi: "",
  nama_en: "",
  deskripsi_en: "",
  namaRules: [(v) => !!v || "Nama harus diisi"],
  deskripsiRules: [(v) => !!v || "Deskripsi harus diisi"],
};
const currentData = reactive({ ...defaultData });

// --- Methods ---
const getDataFromApi = () => {
  const { page, itemsPerPage } = options.value;
  loading.value = true;
  LayananSuperiorLayananService.layananList(
    page,
    itemsPerPage,
    search.value,
    props.id,
  )
    .then((res) => {
      list_data.value = res.data.list_data;
      totalData.value = res.data.total;
    })
    .catch((err) => {
      console.error(err);
    })
    .finally(() => {
      loading.value = false;
      isFirstLoad.value = true;
    });
};

const doAdd = () => {
  Object.assign(currentData, defaultData);
  editedIndex.value = -1;
  dialog.value = true;
};

const doSave = async () => {
  await form.value.validate();

  if (!isValid.value) {
    window.scrollTo(0, 0);
    return;
  }

  const serviceAction = currentData.id
    ? LayananSuperiorLayananService.update(currentData, props.id)
    : LayananSuperiorLayananService.create(currentData, props.id);

  const successMessage = currentData.id
    ? "Update layanan berhasil"
    : "Tambah layanan berhasil";
  const errorMessage = currentData.id
    ? "Update layanan gagal"
    : "Tambah layanan gagal";

  try {
    await serviceAction;
    dialog.value = false;
    CommonService.showSuccessToast(successMessage);
    getDataFromApi();
  } catch (err) {
    console.error(err);
    CommonService.showErrorToast(`${errorMessage}. Coba lagi`);
  }
};

const editItem = (item) => {
  editedIndex.value = list_data.value.indexOf(item);
  editedItem.value = { ...item };
  Object.assign(currentData, {
    ...defaultData,
    ...item,
    nama: item.nama,
    deskripsi: item.deskripsi,
    nama_en: item.nama_en,
    deskripsi_en: item.deskripsi_en,
    id: item.id,
  });
  dialog.value = true;
};

const deleteItem = (item) => {
  editedIndex.value = list_data.value.indexOf(item);
  editedItem.value = { ...item };
  dialogDelete.value = true;
};

const deleteItemConfirm = async () => {
  closeDelete();
  try {
    await LayananSuperiorLayananService.delete(editedItem.value.id);
    CommonService.showSuccessToast("Hapus layanan berhasil");
    getDataFromApi();
  } catch (err) {
    console.error(err);
    CommonService.showErrorToast("Hapus layanan gagal. Coba lagi");
  }
};

const closeDelete = () => {
  dialogDelete.value = false;
  nextTick(() => {
    editedItem.value = {};
    editedIndex.value = -1;
  });
};

// --- Watchers & Lifecycle ---
watch(
  options,
  () => {
    if (isFirstLoad.value) {
      getDataFromApi();
    }
  },
  { deep: true },
);

onMounted(() => {
  getDataFromApi();
});
</script>
